<script setup lang="ts">
import { eventBus } from "#bb/lib/eventBus";
import { useTemplating, buildTemplateContext } from "#bb/composables/useTemplating";
import { resolveResourcePath } from "#bb/lib/pathContract";

const logger = useLogger("RankedReportRaw");
const props = defineProps<{ id: string }>();
const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));

// Inject raw provider report (untouched API response)
const report = inject<any>("report", null);

// Build template context (same pattern as original ranked report)
const templateContext = computed(() => buildTemplateContext(report));
const templating = computed(() => useTemplating(templateContext, { logger }));
const templatedIndividualPath = computed(() => {
  const applyTemplate = templating.value.applyTemplate;
  return applyTemplate(definitionData.value?.individualReportPath || definitionData.value?.individualReportPathRaw || "");
});

const overallConstructId = computed(() => {
  const raw = definitionData.value?.overallConstructId;
  if (raw == null) return null;
  if (typeof raw === "number") return String(raw);
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed.length ? trimmed : null;
  }
  return null;
});

const overallConstructLabel = computed(() => {
  const raw = definitionData.value?.overallConstructLabel;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed.length ? trimmed : null;
});

const commands = [
  {
    id: "editors-group",
    name: "Raw Ranked Report",
    description: "Configure this raw ranked report",
    type: "group",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit ranked raw report",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, { data: definitionData.value });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Ranked Report (Raw)",
    icon: "lucide:file-chart-column-increasing",
  },
});

// Loading state (use provider's lifecycle if present)
const { reportData, loading: storeLoading, fetchReports } = useReportsStore();
const isLoading = computed(() => (report ? (!report.initialized || report.loading) : storeLoading.value));

// We will display raw users directly; do not mutate or map them.
const rankedUsers = ref<any[]>([]);

function toTitleCase(value: string): string {
  return value
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function findConstruct(user: any) {
  const override = overallConstructId.value;
  if (!override) return null;
  const constructs: any[] = Array.isArray(user?.constructs) ? user.constructs : [];
  if (!constructs.length) return null;
  const target = override.toLowerCase();
  return (
    constructs.find((c: any) => String(c?.id ?? "").toLowerCase() === target) ||
    constructs.find((c: any) => String(c?.key ?? "").toLowerCase() === target) ||
    constructs.find((c: any) => String(c?.slug ?? "").toLowerCase() === target) ||
    constructs.find((c: any) => String(c?.name ?? "").toLowerCase() === target)
  );
}

const overallColumnHeading = computed(() => {
  const explicit = overallConstructLabel.value;
  if (explicit) return toTitleCase(explicit);
  const override = overallConstructId.value;
  if (!override) return "Overall";
  for (const candidate of rankedUsers.value) {
    const construct = findConstruct(candidate);
    const name = construct?.name || construct?.label || construct?.title;
    if (typeof name === "string" && name.trim().length) {
      return toTitleCase(name.trim());
    }
  }
  return toTitleCase(override);
});

function computeOverall(user: any): number {
  if (!user) return 0;
  const construct = findConstruct(user);
  if (construct) {
    for (const key of ["percent", "percentage", "score", "value"]) {
      const raw = construct[key as keyof typeof construct];
      const n = Number(raw);
      if (!Number.isNaN(n)) return n > 1 ? n : n * 100;
    }
  }
  // Prefer explicit fields; if absent, average constructs (preserves raw user object)
  for (const val of [user.overall, user.overallScore, user.score]) {
    const n = Number(val);
    if (!Number.isNaN(n)) return n > 1 ? n : n * 100;
  }
  const constructs: any[] = Array.isArray(user.constructs) ? user.constructs : [];
  if (!constructs.length) return 0;
  const selectedIds: string[] = definitionData.value?.selectedConstructIds ?? [];
  const relevant = selectedIds.length ? constructs.filter((c: any) => selectedIds.includes(String(c.id))) : constructs;
  if (!relevant.length) return 0;
  const sum = relevant.reduce((acc: number, c: any) => acc + Number(c.score ?? 0), 0);
  return (sum / relevant.length) * 100;
}

function syncRanking() {
  if (report) {
    // Provider present: use its raw untouched data directly (no mapping, no sorting)
    if (!report.initialized || report.loading) {
      rankedUsers.value = [];
      return;
    }
    const providerItems: any[] = Array.isArray(report.data) ? report.data : [];
    rankedUsers.value = providerItems; // direct reference; DO NOT transform
    return;
  }
  // Fallback (no provider): fetch and use store raw data if needed
  const users: any[] = Array.isArray(reportData.value) ? reportData.value : [];
  rankedUsers.value = users;
}

const sortedUsers = computed(() => {
  const users = Array.isArray(rankedUsers.value) ? rankedUsers.value : [];
  return [...users].sort((a: any, b: any) => {
    const bScore = computeOverall(b);
    const aScore = computeOverall(a);
    return bScore - aScore;
  });
});

onMounted(async () => {
  if (!report) await fetchReports();
  syncRanking();
});

if (report) {
  watch(() => report.data, syncRanking, { deep: true });
  watch(() => report.loading, syncRanking);
  watch(() => report.initialized, syncRanking);
} else {
  watch(reportData, syncRanking);
  const route = useRoute();
  watch(() => route.query.user, async (newUser) => {
    await fetchReports(newUser as string | undefined);
    syncRanking();
  });
}

// Re-render when selected constructs change (affects computeOverall display only)
watch(() => definitionData.value?.selectedConstructIds, syncRanking, { deep: true });
watch(() => definitionData.value?.overallConstructId, syncRanking);

// --- Navigation ---
const { resolve } = useAppRoute();
const features = inject<{
  resourceDefinitionPath?: string | null;
}>("render-features", {});

/** Best-effort stable id for a report row; undefined when absent/blank. */
function userIdOf(user: any): string | undefined {
  const raw = user?.userId ?? user?.id ?? user?.UserId;
  if (raw === undefined || raw === null) return undefined;
  const s = String(raw).trim();
  return s && s !== "undefined" && s !== "null" ? s : undefined;
}

function navigateToUser(user: any) {
  const path = templatedIndividualPath.value;
  if (!path) return;
  const userId = userIdOf(user);
  if (!userId) {
    logger.warn("Ranked report: row has no user id; not navigating", user);
    return;
  }
  const target = resolve(
    resolveResourcePath(path, features.resourceDefinitionPath),
    { app: "reports", fn: "render" },
  ) as {
    path: string;
    query?: Record<string, string>;
  };
  navigateTo({ path: target.path, query: { ...(target.query ?? {}), user: userId } });
}

// --- Assessment timeline helpers ---
const showStarted = computed(() => definitionData.value?.showStarted ?? false);
const showCompleted = computed(() => definitionData.value?.showCompleted ?? false);
const showAnswerCount = computed(() => definitionData.value?.showAnswerCount ?? false);

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

const maxAnswerCount = computed(() => {
  let max = 0;
  for (const user of sortedUsers.value) {
    const count = user?.assessmentTimeline?.answerCount ?? 0;
    if (count > max) max = count;
  }
  return max;
});

function completedDate(user: any): string | null {
  const timeline = user?.assessmentTimeline;
  if (!timeline?.completed) return null;
  const count = timeline.answerCount ?? 0;
  if (maxAnswerCount.value > 0 && count < maxAnswerCount.value) return null;
  return timeline.completed;
}
</script>

<template>
  <table class="w-full text-left table-auto">
    <thead>
      <tr class="border-b">
        <th class="px-4 py-2 w-11">#</th>
        <th class="px-4 py-2 w-72">Name</th>
        <th class="px-4 py-2">Email</th>
        <th class="px-4 py-2">ID Number</th>
        <th v-if="showStarted" class="px-4 py-2">Started</th>
        <th v-if="showCompleted" class="px-4 py-2">Completed</th>
        <th v-if="showAnswerCount" class="px-4 py-2 text-right">Answers</th>
        <th class="px-4 py-2 w-40 text-right">{{ overallColumnHeading }}</th>
      </tr>
    </thead>
    <tbody v-if="isLoading">
      <tr v-for="n in 5" :key="`loading-${n}`" class="border-b">
        <td class="px-4 py-2"><Skeleton class="h-6 w-full" /></td>
        <td class="px-4 py-2"><Skeleton class="h-6 w-full" /></td>
        <td class="px-4 py-2"><Skeleton class="h-6 w-full" /></td>
        <td class="px-4 py-2"><Skeleton class="h-6 w-full" /></td>
        <td v-if="showStarted" class="px-4 py-2"><Skeleton class="h-6 w-full" /></td>
        <td v-if="showCompleted" class="px-4 py-2"><Skeleton class="h-6 w-full" /></td>
        <td v-if="showAnswerCount" class="px-4 py-2 text-right"><Skeleton class="h-6 w-full" /></td>
        <td class="px-4 py-2 text-right"><Skeleton class="h-6 w-full" /></td>
      </tr>
    </tbody>
    <tbody v-else>
      <tr
        v-for="(user, index) in sortedUsers"
        :key="user?.userId ?? user?.emailAddress ?? user?.name ?? index"
        class="border-b cursor-pointer"
        @click="navigateToUser(user)"
      >
        <td class="px-4 py-2">{{ index + 1 }}</td>
        <td class="px-4 py-2">{{ user.name }}</td>
        <td class="px-4 py-2">{{ user.emailAddress }}</td>
        <td class="px-4 py-2">{{ user.idNumber }}</td>
        <td v-if="showStarted" class="px-4 py-2 text-sm text-muted-foreground">{{ formatDate(user?.assessmentTimeline?.started) }}</td>
        <td v-if="showCompleted" class="px-4 py-2 text-sm text-muted-foreground">{{ formatDate(completedDate(user)) }}</td>
        <td v-if="showAnswerCount" class="px-4 py-2 text-right text-sm text-muted-foreground">{{ user?.assessmentTimeline?.answerCount ?? '—' }}</td>
        <td class="px-4 py-2 font-bold text-right">{{ Math.round(computeOverall(user)) }}%</td>
      </tr>
    </tbody>
  </table>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  editor: () => import("#bb/components/editor/reports/ranked/index.vue"),
  componentId: ComponentDefinitionIds.RANKED_REPORT_RAW,
  icon: "lucide:file-chart-column-increasing",
  type: "Ranked Report Raw",
  description: "Displays raw user objects without transforming underlying provider data.",
  default: {},
  categories: ["Reports"],
  order: 12006,
};
</script>
